/**
 * Каталог стартовых страниц роли для интерфейсов /admin и /dashboard.
 * Ключи синхронизированы с backend/services/startPages.js.
 * 'profile' - значение по умолчанию (профиль пользователя).
 */
export type StartPageInterface = 'admin' | 'dashboard';

export const PROFILE_START_PAGE = 'profile';

export interface StartPageOption {
  key: string;
  label: string;
  group: string;
  /** Индекс раздела в AdminPanel / Dashboard */
  index: number;
  /** Подраздел «Отчетов» (только admin) */
  module?: string;
  /** Права (все обязательны), при которых страница доступна роли (совпадают с perms пункта бокового меню). */
  requires?: string[];
}

export const ADMIN_START_PAGES: StartPageOption[] = [
  { key: 'profile', label: 'Профиль пользователя', group: 'Общее', index: 99 },
  { key: 'home', label: 'Главная', group: 'Общее', index: 20, requires: ['home.menu'] },

  { key: 'users', label: 'Пользователи', group: 'Пользователи', index: 2, requires: ['users.menu'] },

  { key: 'reports', label: 'Отчеты (главная)', group: 'Отчеты', index: 3, requires: ['reports.menu'] },
  { key: 'reports.fill', label: 'Отчеты → Заполнение', group: 'Отчеты', index: 3, module: 'empty', requires: ['reports.menu', 'reports.fill_monitoring'] },
  { key: 'reports.edit_daily', label: 'Отчеты → Редактирование', group: 'Отчеты', index: 3, module: 'edit', requires: ['reports.menu', 'reports.edit_daily'] },
  { key: 'reports.edit_monthly', label: 'Отчеты → Ред. мес. отчет', group: 'Отчеты', index: 3, module: 'editmonth', requires: ['reports.menu', 'reports.edit_monthly'] },
  { key: 'reports.summary', label: 'Отчеты → Отчет', group: 'Отчеты', index: 3, module: 'view', requires: ['reports.menu', 'reports.view_summary'] },
  { key: 'reports.director', label: 'Отчеты → Дир. отчет', group: 'Отчеты', index: 3, module: 'editnew', requires: ['reports.menu', 'reports.view_director'] },
  { key: 'reports.delivery', label: 'Отчеты → Сверка доставок', group: 'Отчеты', index: 3, module: 'compare', requires: ['reports.menu', 'reports.delivery.view'] },
  { key: 'reports.math', label: 'Отчеты → Мат. отчет', group: 'Отчеты', index: 3, module: 'MathReport', requires: ['reports.menu', 'reports.math.view'] },

  { key: 'tables', label: 'Таблица', group: 'Таблицы и логи', index: 4, requires: ['tables.menu'] },
  { key: 'logs', label: 'Логи', group: 'Таблицы и логи', index: 5, requires: ['system.logs.view'] },
  { key: 'audit', label: 'Журнал действий', group: 'Таблицы и логи', index: 111, requires: ['audit.menu', 'audit.view'] },

  { key: 'documents.invoices', label: 'Документы → Документы', group: 'Документы', index: 103, requires: ['documents.menu', 'documents.invoices.view'] },
  { key: 'documents.marking', label: 'Документы → КМ\'ы', group: 'Документы', index: 106, requires: ['documents.menu', 'documents.marking.view'] },

  { key: 'timesheet', label: 'Табель РБ', group: 'Табель РБ', index: 101, requires: ['timesheet.menu'] },
  { key: 'journal', label: 'Электронный журнал', group: 'Журнал', index: 105, requires: ['journal.menu', 'journal.view'] },

  { key: 'guide.remains', label: 'Справка → Остатки', group: 'Справка', index: 10, requires: ['guide.remains.menu', 'guide.remains.view'] },
  { key: 'guide.nomenclature', label: 'Справка → Справочник', group: 'Справка', index: 11, requires: ['guide.nomenclature.menu', 'guide.nomenclature.view'] },

  { key: 'system.tasks', label: 'Админ → Планировщик', group: 'Админ', index: 7, requires: ['system.menu', 'system.tasks.manage'] },
  { key: 'system.dangerous', label: 'Админ → Опасные функции', group: 'Админ', index: 100, requires: ['system.menu', 'system.dangerous.execute'] },
  { key: 'system.ofd', label: 'Админ → Данные ОФД', group: 'Админ', index: 102, requires: ['system.menu'] },
  { key: 'system.whatsapp', label: 'Админ → WhatsApp Боты', group: 'Админ', index: 104, requires: ['system.menu', 'system.whatsapp.manage'] },
  { key: 'system.notifications', label: 'Админ → Уведомления', group: 'Админ', index: 107, requires: ['system.menu', 'system.notifications.send'] },
  { key: 'system.roles', label: 'Админ → Конструктор ролей', group: 'Админ', index: 110, requires: ['system.menu', 'system.roles.manage'] }
];

/**
 * Кабинет аптеки (/dashboard): своя матрица прав и свои стартовые страницы - пункты DashboardSidebar
 * в том же порядке. Если стартовая страница роли недоступна, кабинет открывает первую доступную из списка.
 */
export const DASHBOARD_START_PAGES: StartPageOption[] = [
  { key: 'profile', label: 'Профиль пользователя', group: 'Общее', index: 99 },
  { key: 'home', label: 'Главная', group: 'Общее', index: 20, requires: ['home.menu'] },

  { key: 'reports.current', label: 'Отчетность → Текущий отчет', group: 'Отчетность', index: 0, requires: ['reports.menu', 'reports.current.view'] },
  { key: 'reports.monthly', label: 'Отчетность → Отчет за месяц', group: 'Отчетность', index: 1, requires: ['reports.menu', 'reports.monthly.view'] },

  { key: 'documents.flow', label: 'Документы → Документооборот', group: 'Документы', index: 7, requires: ['documents.menu', 'documents.flow.view'] },
  { key: 'documents.marking', label: 'Документы → КМ\'ы', group: 'Документы', index: 12, requires: ['documents.menu', 'documents.marking.view'] },
  { key: 'timesheet', label: 'Документы → Табель РБ', group: 'Документы', index: 3, requires: ['timesheet.menu'] },
  { key: 'journal', label: 'Документы → Электронный журнал', group: 'Документы', index: 10, requires: ['journal.menu'] },

  { key: 'monitoring.cash', label: 'Мониторинг → Касса', group: 'Мониторинг', index: 4, requires: ['monitoring.menu', 'monitoring.cash.view'] },
  { key: 'monitoring.km', label: 'Мониторинг → КМ', group: 'Мониторинг', index: 5, requires: ['monitoring.menu', 'monitoring.km.view'] },

  { key: 'guide.remains', label: 'Справочник → Остатки', group: 'Справочник', index: 6, requires: ['guide.remains.menu'] },
  { key: 'guide.nomenclature', label: 'Справочник → Номенклатура', group: 'Справочник', index: 9, requires: ['guide.nomenclature.menu'] },

  { key: 'staff', label: 'Сотрудники', group: 'Точка', index: 13, requires: ['staff.menu'] }
];

export const getStartPageOptions = (iface: StartPageInterface): StartPageOption[] =>
  iface === 'admin' ? ADMIN_START_PAGES : DASHBOARD_START_PAGES;

export const findStartPage = (iface: StartPageInterface, key?: string | null): StartPageOption | undefined => {
  if (!key) return undefined;
  return getStartPageOptions(iface).find(o => o.key === key);
};

/** Права, которых не хватает роли, чтобы страница была доступна (пусто = доступна). */
export const getMissingStartPagePerms = (
  option: StartPageOption,
  hasPerm: (code: string) => boolean
): string[] => (option.requires || []).filter(code => !hasPerm(code));

/** Первая доступная страница интерфейса, кроме профиля: порядок каталога совпадает с боковым меню */
export const findFirstAvailablePage = (
  iface: StartPageInterface,
  hasPerm: (code: string) => boolean
): StartPageOption | undefined =>
  getStartPageOptions(iface).find(o => o.key !== PROFILE_START_PAGE && getMissingStartPagePerms(o, hasPerm).length === 0);
